import { LockerLogSchema, type LockerLog } from '../schemas/lockerLog';
import { deliveryDetailMockBoxIds } from './deliveryDetail';

// Realistic samples in the wire shape (grouped by box↔lock pairing period), exercising every UI
// branch: two pairing groups so the flattening shows, a multi-sentence STEPS paragraph (split into
// rows), one whose sentences quote their own values (the `[Box Booked]` shape the `","` delimiter
// exists for), an HTML-escaped one (entities carried raw — the UI decodes), a single-sentence event,
// an unparseable line (fail-open raw row), an actorless event (empty `user_full_name` → «—»), and an
// empty box. Entries are written newest-first — the order
// the backend guarantees and the panel paints verbatim; `date`/`time` are already formatted in the
// locker's local time. STEPS paragraphs stay oldest-first internally; the UI reverses them.
// Fictional data only — never real «Usuario» PII in fixtures.
const RICH = [
  {
    box_id: deliveryDetailMockBoxIds.collected,
    lock_id: 'JJ12KK',
    is_paired: true,
    occurred_on: '2026-06-12',
    logs: [
      // HTML-escaped on the wire (Django `escape()` — apostrophes as `&#x27;`), kept raw here:
      // decoding is the UI's job, so a fixture that pre-decoded would hide the regression.
      // Fictional values.
      {
        id: 908,
        user_full_name: 'Tech Team',
        date: '12/06/2026',
        time: '19:15:02',
        registry:
          'Transaction updated: {&#x27;state&#x27;: &#x27;finalizado&#x27;, &#x27;info_att&#x27;: None, &#x27;observations&#x27;: &#x27;12/06/2026 19:15 | Tech Team | Modificación de Transacción&#x27;, &#x27;company&#x27;: {&#x27;id&#x27;: 17, &#x27;name&#x27;: &#x27;GLS&#x27;, &#x27;type&#x27;: &#x27;courier&#x27;, &#x27;delivery_price&#x27;: 0.4}}',
      },
      // The `[Box Booked]` shape: a STEPS paragraph whose sentences quote their OWN values, so
      // `","` is the only thing that separates them. Newest of the group — the «buzón» freed by
      // the relocation below is booked for the next expedition. Fictional ids.
      {
        id: 907,
        user_full_name: 'Sistema',
        date: '12/06/2026',
        time: '19:02:11',
        registry:
          'STEPS: "[Box Booked] Box booked for booking "b71c04af-3d52-4e08-9a16-5cf0d2e771bb" related to expedition "4ae9f130-8c67-42d1-b0a5-91d7e6c34f82" with external reference "20250931199"","[Box Booked] Box booked for booking "b71c04af-3d52-4e08-9a16-5cf0d2e771bb" related to expedition "4ae9f130-8c67-42d1-b0a5-91d7e6c34f82" with external reference "N/A""',
      },
      {
        id: 906,
        user_full_name: 'Carlos Amado',
        date: '12/06/2026',
        time: '18:41:35',
        registry: 'Relocating parcel from box DHP10 to box DHP14',
      },
      {
        id: 905,
        user_full_name: 'Carlos Amado',
        date: '12/06/2026',
        time: '12:30:46',
        registry:
          'STEPS: "A courier is trying to make a delivery. Params: location_id: 10729, tracking: STgMQqJm1F_001_v, recipient_name: Jorge Luis Godoy Madrid, recipient_phone: +34665140463, location_must_belong_to_user: true, size: S", "Delivering in box DHP10.", "Opening code requested, the code provided was 8733", "Occupied box with ID 59145 because TransactionDeliveredEvent", "Buzón DHP10 ha cambiado a estado ocupado"',
      },
      {
        id: 904,
        user_full_name: 'Sistema',
        date: '12/06/2026',
        time: '12:29:16',
        registry: 'Door opening angle detected.',
      },
      {
        id: 903,
        user_full_name: 'Sistema',
        date: '12/06/2026',
        time: '12:29:06',
        registry: 'STEPS: "ReleaseBoxUnsyncFlags", "Lock synchronized correctly"',
      },
    ],
  },
  {
    box_id: deliveryDetailMockBoxIds.collected,
    lock_id: 'AB12CD',
    is_paired: false,
    occurred_on: '2026-06-11',
    logs: [
      {
        id: 902,
        user_full_name: 'Sistema',
        date: '11/06/2026',
        time: '21:12:41',
        registry: 'Raw trace with an "unbalanced quote',
      },
      {
        id: 901,
        user_full_name: '',
        date: '11/06/2026',
        time: '21:12:40',
        registry: 'BLE secure channel established.',
      },
    ],
  },
];
// The two empty flavours the wire distinguishes (Equipo P, 2026-07-28): a «buzón» with neither
// lock nor history is a bare empty array, while one WITH a lock but no recorded history is a
// single pairing period carrying `occurred_on: null`. Both land on the same «Sin registros» UI.
const EMPTY: unknown[] = [];
const PAIRED_WITHOUT_HISTORY = [
  {
    box_id: deliveryDetailMockBoxIds.nonCollected,
    lock_id: 'ZZ90YY',
    is_paired: true,
    occurred_on: null,
    logs: [],
  },
];

// Keyed by BOX id (the endpoint reads by box, not by delivery). `stored` covers the `blocked`
// fixture too — both are «Matrícula» DIZ03, the same physical «buzón», so they share one log.
const RAW_DATASET: Record<string, unknown> = {
  [deliveryDetailMockBoxIds.collected]: RICH,
  [deliveryDetailMockBoxIds.stored]: RICH,
  [deliveryDetailMockBoxIds.expired]: RICH,
  [deliveryDetailMockBoxIds.retrieved]: EMPTY,
  [deliveryDetailMockBoxIds.nonCollected]: PAIRED_WITHOUT_HISTORY,
};

export async function fetchLockerLogMock(boxId: string): Promise<LockerLog> {
  // Simulate network latency for a realistic dev experience.
  await new Promise((resolve) => setTimeout(resolve, 100));
  return LockerLogSchema.parse(RAW_DATASET[boxId] ?? RICH);
}
